<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import { useDismissablePopover } from '@/composables/useDismissablePopover'
import { useLocale } from '@/composables/useLocale'
import { useSupportUnreadCount } from '@/composables/useSupportUnreadCount'
import type { CreditBalance } from '@/types/newsletter'

// Mobile-only (< lg) profile menu. Balance is supplied by the parent so that
// no additional credit-balance request is made from here.
defineProps<{
  balance: CreditBalance | null
  loading: boolean
}>()

const { t } = useI18n()
const router = useRouter()
const { logout, currentOrganisation } = useAuth()
const { currentLocale, supportedLocales, switchLocale } = useLocale()

// Read from the module-level singleton - never fetch here
const { hasUnread } = useSupportUnreadCount()

const triggerRef = ref<HTMLButtonElement | null>(null)
const menuRef = ref<HTMLElement | null>(null)

// Arrow-key navigation between menu items; Escape is handled by the popover composable.
function handleKeydown(event: KeyboardEvent): void {
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    const items = Array.from(menuRef.value?.querySelectorAll<HTMLElement>('[role="menuitem"], [role="menuitemradio"]') ?? [])
    if (items.length === 0) return
    event.preventDefault()
    const index = items.indexOf(document.activeElement as HTMLElement)
    const next = event.key === 'ArrowDown'
      ? (index + 1) % items.length
      : (index - 1 + items.length) % items.length
    items[next]?.focus()
  }
}

// Listeners exist only while the menu is open.
const menu = useDismissablePopover({
  anchorRef: triggerRef,
  popoverRef: menuRef,
  singleOpen: true,
  closeOnEscape: true,
  returnFocusOnEscape: true,
  closeOnOutside: ['mousedown', 'touchstart'],
  onKeydown: handleKeydown,
})
const menuOpen = menu.isOpen

function closeMenu(returnFocus = false): void {
  menu.close({ returnFocus })
}

function toggleMenu(): void {
  menu.toggle()
}

function goToCredits(): void {
  const slug = currentOrganisation.value?.slug
  closeMenu()
  if (slug) {
    void router.push(`/organizer/organisations/${slug}/credits`)
  }
}

function goTo(path: string): void {
  closeMenu()
  void router.push(path)
}

function handleLanguageChange(locale: typeof supportedLocales[number]): void {
  switchLocale(locale)
  closeMenu(true)
}

async function handleLogout(): Promise<void> {
  closeMenu()
  try {
    await logout()
  } finally {
    void router.push('/organizer/login')
  }
}
</script>

<template>
  <div class="relative lg:hidden">
    <button
      ref="triggerRef"
      type="button"
      class="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
      aria-haspopup="menu"
      aria-controls="organizer-profile-menu"
      :aria-expanded="menuOpen"
      :aria-label="t('nav.profile_menu.button')"
      @click="toggleMenu"
    >
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.121 17.804A9 9 0 1118.879 17.8M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </button>

    <div
      v-if="menuOpen"
      id="organizer-profile-menu"
      ref="menuRef"
      role="menu"
      :aria-label="t('nav.profile_menu.button')"
      class="absolute right-0 mt-2 w-56 rounded-lg bg-slate-800 border border-slate-700 shadow-xl py-1 z-40"
    >
      <!-- Credits -->
      <button
        v-if="currentOrganisation"
        type="button"
        role="menuitem"
        class="w-full flex items-center gap-2 px-4 py-2 text-sm text-left transition-colors hover:bg-slate-700 focus:bg-slate-700 focus:outline-none"
        :class="balance && balance.balance < 100 ? 'text-amber-400' : 'text-slate-300 hover:text-white'"
        @click="goToCredits"
      >
        <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span class="font-medium">
          <span v-if="loading && balance === null">-</span>
          <span v-else>{{ t('credits.chip.balance_label', { balance: balance?.balance.toLocaleString() ?? '-' }) }}</span>
        </span>
      </button>

      <!-- Help -->
      <div class="border-t border-slate-700 mt-1 pt-1" role="group" :aria-label="t('nav.profile_menu.help')">
        <p class="px-4 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500" aria-hidden="true">
          {{ t('nav.profile_menu.help') }}
        </p>
        <button
          type="button"
          role="menuitem"
          class="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white focus:bg-slate-700 focus:outline-none transition-colors"
          @click="goTo('/organizer/docs')"
        >
          {{ t('nav.documentation') }}
        </button>
        <button
          type="button"
          role="menuitem"
          class="w-full flex items-center gap-2 text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white focus:bg-slate-700 focus:outline-none transition-colors"
          @click="goTo('/organizer/support')"
        >
          {{ t('nav.support') }}
          <span
            v-if="hasUnread"
            class="w-1.5 h-1.5 rounded-full bg-emerald-400"
            :aria-label="t('support.nav.unread')"
          />
        </button>
        <button
          type="button"
          role="menuitem"
          class="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white focus:bg-slate-700 focus:outline-none transition-colors"
          @click="goTo('/organizer/about')"
        >
          {{ t('nav.about_startline') }}
        </button>
      </div>

      <!-- Language -->
      <div class="border-t border-slate-700 mt-1 pt-1" role="group" :aria-label="t('nav.profile_menu.language')">
        <p class="px-4 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500" aria-hidden="true">
          {{ t('nav.profile_menu.language') }}
        </p>
        <button
          v-for="locale in supportedLocales"
          :key="locale"
          type="button"
          role="menuitemradio"
          :aria-checked="currentLocale === locale"
          class="w-full text-left px-4 py-2 text-sm transition-colors focus:outline-none focus:bg-slate-700"
          :class="currentLocale === locale
            ? 'bg-slate-700 text-white font-semibold'
            : 'text-slate-300 hover:bg-slate-700 hover:text-white'"
          @click="handleLanguageChange(locale)"
        >
          {{ t(`languages.${locale}`) }}
        </button>
      </div>

      <!-- Logout -->
      <div class="border-t border-slate-700 mt-1 pt-1">
        <button
          type="button"
          role="menuitem"
          class="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white focus:bg-slate-700 focus:outline-none transition-colors"
          @click="handleLogout"
        >
          {{ t('auth.logout.button') }}
        </button>
      </div>
    </div>
  </div>
</template>
